/**
 * AuthSuccessPanel — the terminal "it worked, here's what to do next" state
 * of an auth flow: a tinted icon disc, a centred headline, a centred
 * explanation, then the action stack.
 *
 * forgot-password (link sent) and reset-password (password changed) rendered
 * this twice, down to the vertical centring and the safe-area padding. Only
 * the icon, its tint and the actions ever differed, so those are the props.
 */
import type { ReactNode } from 'react'
import { View } from 'react-native'
import Animated, { FadeIn, FadeInDown, useReducedMotion } from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { FONTS, SPACING } from '../../lib/brand'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, DURATION, STAGGER } from '../../lib/theme/tokens'

interface AuthSuccessPanelProps {
  /** Glyph rendered inside the disc, already sized and coloured. */
  icon: ReactNode
  /** Tint of the disc behind the icon. */
  iconBackground: string
  title: string
  description: string
  /** The action stack under the copy. */
  children: ReactNode
  testID?: string
}

export function AuthSuccessPanel({
  icon,
  iconBackground,
  title,
  description,
  children,
  testID,
}: AuthSuccessPanelProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { isDesktop } = useBreakpoint()
  const insets = useSafeAreaInsets()
  const reducedMotion = useReducedMotion()

  const isWide = isDesktop

  return (
    <View
      style={
        isWide
          ? { flex: 1, justifyContent: 'center', alignItems: 'center' }
          : {
              flex: 1,
              paddingTop: insets.top,
              paddingBottom: insets.bottom + SPACING.lg,
              paddingHorizontal: SPACING.lg,
              justifyContent: 'center',
            }
      }
    >
      <View style={{ alignItems: 'center', width: '100%' }} testID={testID}>
        <Animated.View
          collapsable={false}
          entering={reducedMotion ? undefined : FadeIn.duration(DURATION.slow).delay(STAGGER * 2)}
          style={{
            width: 80,
            height: 80,
            borderRadius: 40,
            backgroundColor: iconBackground,
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: SPACING.xl - SPACING.xs,
          }}
          accessible={false}
        >
          {icon}
        </Animated.View>

        <Animated.Text
          entering={
            reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 4)
          }
          accessibilityRole="header"
          numberOfLines={2}
          adjustsFontSizeToFit
          minimumFontScale={0.88}
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: isWide ? 28 : 30,
            lineHeight: isWide ? 34 : 36,
            textAlign: 'center',
            color: colors.textPrimary,
          }}
        >
          {title}
        </Animated.Text>

        <Animated.Text
          entering={
            reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 6)
          }
          style={{
            fontFamily: FONTS.body,
            fontSize: 15,
            lineHeight: 22,
            textAlign: 'center',
            marginTop: SPACING.sm + 2,
            marginBottom: SPACING.xl + SPACING.xs,
            paddingHorizontal: SPACING.sm + SPACING.xs,
            color: colors.textSecondary,
          }}
        >
          {description}
        </Animated.Text>

        {children}
      </View>
    </View>
  )
}
